import type { FileMetadata } from './protocol';

/**
 * A FileSink is where received chunks are written. Two implementations:
 *
 *  - DiskFileSink  — streams straight to disk via the File System Access API.
 *    Memory stays O(1); this is what makes 50GB+ downloads possible. Supports
 *    positional writes so resume-after-reconnect can seek to any offset.
 *
 *  - MemoryFileSink — fallback for browsers without showSaveFilePicker (e.g.
 *    Firefox/Safari). Buffers chunks and assembles a Blob at the end, so it is
 *    only suitable for files that comfortably fit in RAM.
 *
 * The receiver picks DiskFileSink when available, otherwise MemoryFileSink.
 */
export interface FileSink {
  /** Whether this sink streams to disk (true) or buffers in memory (false). */
  readonly streaming: boolean;
  /** Write one chunk's payload at the given chunk index. */
  write(index: number, chunkSize: number, data: Uint8Array): Promise<void>;
  /** Finalize. Returns an object URL for download when buffered in memory. */
  finalize(): Promise<{ objectUrl?: string }>;
  /** Re-read the finalized output for integrity hashing. */
  readBack(): Promise<Blob>;
  /** Discard any partial output. */
  abort(): Promise<void>;
}

export class DiskFileSink implements FileSink {
  readonly streaming = true;
  private writable: FileSystemWritableFileStream | null = null;

  private constructor(
    private readonly handle: FileSystemFileHandleLike,
    writable: FileSystemWritableFileStream,
  ) {
    this.writable = writable;
  }

  static async create(
    handle: FileSystemFileHandleLike,
    keepExistingData = false,
  ): Promise<DiskFileSink> {
    const writable = await handle.createWritable({ keepExistingData });
    return new DiskFileSink(handle, writable);
  }

  async write(index: number, chunkSize: number, data: Uint8Array): Promise<void> {
    if (!this.writable) throw new Error('sink closed');
    const position = index * chunkSize;
    // Positional write: robust regardless of arrival order, and the basis for
    // resume (we can re-open with keepExistingData and seek to any chunk).
    // Wrap in a Blob so the bytes are accepted regardless of the Uint8Array's
    // backing-buffer type (avoids the ArrayBuffer/SharedArrayBuffer generic).
    await this.writable.write({ type: 'write', position, data: new Blob([data as BlobPart]) });
  }

  async finalize(): Promise<{ objectUrl?: string }> {
    if (this.writable) {
      await this.writable.close();
      this.writable = null;
    }
    return {};
  }

  async readBack(): Promise<Blob> {
    // Streaming the file back lets us hash 50GB without buffering it.
    return this.handle.getFile();
  }

  async abort(): Promise<void> {
    try {
      await this.writable?.close();
    } catch {
      /* ignore */
    }
    this.writable = null;
  }

  get fileHandle(): FileSystemFileHandleLike {
    return this.handle;
  }
}

export class MemoryFileSink implements FileSink {
  readonly streaming = false;
  // Sparse array indexed by chunk index keeps reassembly order-independent.
  private parts: Uint8Array[] = [];
  private blob: Blob | null = null;
  private objectUrl: string | null = null;

  constructor(private readonly meta: FileMetadata) {}

  async write(index: number, _chunkSize: number, data: Uint8Array): Promise<void> {
    // Copy out of the (possibly pooled) receive buffer to retain it safely.
    this.parts[index] = data.slice();
  }

  async finalize(): Promise<{ objectUrl?: string }> {
    this.blob = new Blob(this.parts as BlobPart[], {
      type: this.meta.mime || 'application/octet-stream',
    });
    this.parts = [];
    this.objectUrl = URL.createObjectURL(this.blob);
    return { objectUrl: this.objectUrl };
  }

  async readBack(): Promise<Blob> {
    if (!this.blob) throw new Error('sink not finalized');
    return this.blob;
  }

  async abort(): Promise<void> {
    this.parts = [];
    this.blob = null;
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}

export function supportsFileSystemAccess(): boolean {
  return typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function';
}

/**
 * Prompt the user to choose a save location and return a streaming disk sink.
 * Must be called from a user gesture (click). Throws if the user cancels or the
 * API is unavailable.
 */
export async function createDiskSink(meta: FileMetadata): Promise<DiskFileSink> {
  if (!supportsFileSystemAccess()) throw new Error('File System Access API unavailable');
  const handle = await window.showSaveFilePicker!({
    suggestedName: meta.name,
    types: meta.mime
      ? [{ description: 'File', accept: { [meta.mime]: [] } }]
      : undefined,
  });
  return DiskFileSink.create(handle);
}
